import { useMutation, useQuery } from '@tanstack/react-query'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'
import { FacilityIncludedSchema, FacilitySchema } from '../types/facility.type'
import { GeneralResMock } from '../types/general.type'

type Prop = {
  id?: string
  page?: number
  limit?: number
  query?: string
  sort_by?: 'asc' | 'desc'
  sort_dir?: string
  name?: string
  facility_type?: string
}
export const useFacility = (arg: Prop = {}) => {
  const { id, page, limit, query, sort_by, sort_dir, name, facility_type } = arg ?? {}
  const { data, isLoading: facilitiesIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<FacilitySchema[], FacilityIncludedSchema[]>>(
        'facilities',
        {
          params: {
            ...(page && { 'page[number]': page }),
            ...(limit && { 'page[size]': limit }),
            ...(query && { query }),
            ...(sort_dir && { sort_dir }),
            ...(sort_by && { sort_by }),
            ...(name && { name }),
            ...(facility_type && { facility_type })
          }
        }
      )
      return data
    },
    queryKey: ['facilities', { page, limit, query, sort_by, sort_dir, name, facility_type }]
  })

  const { data: singleFacility, isLoading: singleFacilityIsLoading } = useQuery({
    queryFn: async () => {
      console.log(id)
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<FacilitySchema>>(`facilities/${id}`)
      return data.data
    },
    enabled: !!id,
    queryKey: ['facilities', { id }]
  })

  const { mutateAsync: createFacility } = useMutation({
    mutationFn: async (body: CreateFacilityRequest) => {
      const { data } = await AXIOS_INSTANCE.post('facilities', body, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] })
    }
  })

  const { mutateAsync: updateFacility } = useMutation({
    mutationFn: async (body: CreateFacilityRequest) => {
      const { data } = await AXIOS_INSTANCE.patch(`facilities/${body.data.id}`, body, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] })
      queryClient.invalidateQueries({ queryKey: ['facilities', { id }] })
    }
  })

  const { mutateAsync: deleteFacility } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.patch(`facilities/${id}/deactivate`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] })
    }
  })
  return {
    facilities: data?.data,
    facilitiesIsLoading,
    facilitiesMeta: data?.meta,
    facilitiesPagination: data?.meta?.pagination,
    facilitiesIncluded: data?.included,
    singleFacility,
    singleFacilityIsLoading,
    createFacility,
    updateFacility,
    deleteFacility
  }
}

export type CreateFacilityRequest = {
  data: {
    type: string
    id?: string
    attributes: {
      name: string
      facility_type: string
      status: string
      location: {
        address: {
          line1: string
          line2: string
          city: string
          state: string
          postal_code: string
          country: string
        }
        coordinates?: {
          latitude: number
          longitude: number
        }
      }
      report_branding?: {
        letterhead_layout: 'logo_left' | 'logo_right' | 'stacked'
        signature_layout: 'left' | 'right' | 'center'
      }
    }
  }
}
